import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, BookOpen, Bot, ChevronRight, Loader2 } from 'lucide-react';
import { useAuth } from '../lib/auth';
import '../components/future-ready/future-ready.css';

const API = process.env.REACT_APP_BACKEND_URL;

/* Pathway-specific wiring; the two-button screen and reader are shared. */
export const PATHWAYS = {
  'job-roles': {
    label: 'Future Ready Pathway',
    directory: '/job-roles',
    nameUrl: (p) => `${API}/api/roles/${p.catalogNumber}`,
    nameOf: (d) => d?.role?.role_name || d?.role_name || d?.name,
    previewUrl: (p) => `/job-roles/preview/${p.catalogNumber}`,
    automationUrl: (p) => `/job-roles/automation/${p.catalogNumber}`,
    automationApi: (p) => `${API}/api/future-ready/automation/${p.catalogNumber}`,
    button1: 'How This Career Works with an AI-Agent Workforce',
    button2: 'How an AI-Agent Workforce Could Take Over This Role and Eliminate the Position',
    noun: 'role',
  },
  specializations: {
    label: 'Inner Specialisations',
    directory: '/specializations',
    nameUrl: (p) => `${API}/api/specs/${p.program}_${p.id}`,
    nameOf: (d) => d?.title || d?.name,
    previewUrl: (p) => `/specializations/preview/${p.program}/${p.id}`,
    automationUrl: (p) => `/specializations/automation/${p.program}/${p.id}`,
    automationApi: (p) => `${API}/api/specs/automation/${p.program}_${p.id}`,
    button1: 'How This Specialisation Works with an AI-Agent Workforce',
    button2: 'How AI Agents Could Automate Work in This Specialisation and Replace Related Roles',
    noun: 'specialisation',
  },
};

const lockMessage = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (e?.response?.status === 401) return 'Your session expired — please reload the page.';
  return typeof d === 'string' ? d : (d?.message || fallback);
};

export const PredictionBadge = ({ testId = 'prediction-scenario-badge' }) => (
  <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-extrabold bg-amber-100 text-amber-900 border border-amber-200 px-2.5 py-1 rounded-full" data-testid={testId}>
    <Bot size={12} /> Prediction scenario
  </span>
);

export default function EntryChoicePage({ pathway }) {
  const cfg = PATHWAYS[pathway];
  const params = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [name, setName] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let live = true;
    axios.get(cfg.nameUrl(params))
      .then((r) => { if (live) setName(cfg.nameOf(r.data) || ''); })
      .catch((e) => { if (live) setErr(lockMessage(e, `This ${cfg.noun} is not in your current selection.`)); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [user, pathway, params.catalogNumber, params.program, params.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { document.title = name ? `${name} · ${cfg.label}` : cfg.label; }, [name, cfg.label]);

  return (
    <div className="frd-page" style={{ height: 'auto', minHeight: '100vh', overflow: 'visible' }} data-testid="entry-choice-page">
      <main className="frd-directory" style={{ height: 'auto', maxWidth: 760 }}>
        <div className="frd-topline">
          <Link to={cfg.directory} data-testid="entry-choice-back"><ArrowLeft size={16} /> All {cfg.noun}s</Link>
        </div>
        {loading ? <div className="frd-loading" data-testid="entry-choice-loading"><Loader2 className="animate-spin" size={18} /> Loading…</div>
          : err ? <div className="frd-notice frd-error" data-testid="entry-choice-error">{err}<button type="button" onClick={() => navigate(cfg.directory)} className="mt-2 underline">Back to your selection</button></div>
          : (
            <section className="frd-directory-heading" style={{ padding: '22px 20px' }}>
              <p className="frd-eyebrow">{cfg.label} · Choose how to read</p>
              <h1 data-testid="entry-choice-name">{name}</h1>
              <p style={{ marginBottom: 18 }} data-testid="entry-choice-explanation">Explore how the work operates, or read a conditional automation scenario. Neither choice locks other pathways.</p>
              <div className="entry-choice-buttons">
                <button type="button" className="entry-choice-btn entry-choice-btn-1" onClick={() => navigate(cfg.previewUrl(params))} data-testid="entry-choice-button-1">
                  <BookOpen size={20} />
                  <span>{cfg.button1}</span>
                  <ChevronRight size={18} />
                </button>
                <button type="button" className="entry-choice-btn entry-choice-btn-2" onClick={() => navigate(cfg.automationUrl(params))} data-testid="entry-choice-button-2">
                  <Bot size={20} />
                  <span>{cfg.button2}<br /><PredictionBadge testId="entry-choice-prediction-badge" /></span>
                  <ChevronRight size={18} />
                </button>
              </div>
            </section>
          )}
      </main>
    </div>
  );
}
